import { useState, useEffect } from 'react';
import {
  Link2,
  QrCode,
  Download,
  Copy,
  Check,
  ExternalLink,
  Loader2,
  Search,
  Plus,
  Trash2,
} from 'lucide-react';

import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Card, CardContent } from '../ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { getAuthHeaders } from '../../services/adminApi';

import { PageTitle } from './shared';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

// Link type display labels and colors
const LINK_TYPE_CONFIG = {
  quiz: { label: 'Quiz', color: 'bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-300' },
  scenario: { label: 'Scenario', color: 'bg-purple-100 text-purple-800 dark:bg-purple-900/50 dark:text-purple-300' },
  vocabulary: { label: 'Vocabulary', color: 'bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-300' },
  resource: { label: 'Resource', color: 'bg-amber-100 text-amber-800 dark:bg-amber-900/50 dark:text-amber-300' },
  learning_section: { label: 'Section', color: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300' },
};

// Types that need a target_id (specific content item)
const TARGET_ID_TYPES = ['quiz', 'scenario', 'resource'];

const ResourceLinksManager = () => {
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [copiedId, setCopiedId] = useState(null);
  const [qrPreview, setQrPreview] = useState(null);
  const [qrLoading, setQrLoading] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [targets, setTargets] = useState([]);
  const [newLink, setNewLink] = useState({
    link_type: '',
    target_id: '',
    title: '',
    description: '',
  });

  useEffect(() => {
    fetchLinks();
  }, []);

  // Fetch available targets when link type changes
  const fetchTargets = async (linkType) => {
    if (!TARGET_ID_TYPES.includes(linkType)) {
      setTargets([]);
      return;
    }
    const collectionMap = { quiz: 'quizzes', scenario: 'scenarios', resource: 'resources' };
    const endpoint = linkType === 'resource'
      ? `${API_BASE}/api/admin/${collectionMap[linkType] || 'resources'}`
      : `${API_BASE}/api/admin/${collectionMap[linkType]}`;
    try {
      const response = await fetch(endpoint, { headers: getAuthHeaders() });
      if (response.ok) {
        const data = await response.json();
        const items = Array.isArray(data) ? data : (data.items || data.results || []);
        setTargets(items.map(item => ({ id: item.id, title: item.title || item.word || item.name || item.id })));
      }
    } catch (err) {
      console.error('Failed to fetch targets:', err);
      setTargets([]);
    }
  };

  const handleCreateLink = async () => {
    if (!newLink.link_type || !newLink.title) return;
    setCreating(true);
    try {
      const body = {
        link_type: newLink.link_type,
        title: newLink.title,
        description: newLink.description || undefined,
      };
      if (newLink.target_id) body.target_id = newLink.target_id;

      const response = await fetch(`${API_BASE}/api/admin/resource-links`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (response.ok) {
        setShowCreate(false);
        setNewLink({ link_type: '', target_id: '', title: '', description: '' });
        setTargets([]);
        fetchLinks();
      }
    } catch (err) {
      console.error('Failed to create link:', err);
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteLink = async (linkId) => {
    if (!window.confirm('Deactivate this resource link?')) return;
    try {
      await fetch(`${API_BASE}/api/admin/resource-links/${linkId}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      fetchLinks();
    } catch (err) {
      console.error('Failed to delete link:', err);
    }
  };

  const fetchLinks = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/admin/resource-links`, {
        headers: getAuthHeaders(),
      });
      if (response.ok) {
        const data = await response.json();
        setLinks(data);
      }
    } catch (error) {
      console.error('Failed to fetch resource links:', error);
    } finally {
      setLoading(false);
    }
  };

  const copyShareUrl = (link) => {
    const url = `https://members.owazamartialarts.com/r/${link.short_code}`;
    navigator.clipboard.writeText(url);
    setCopiedId(link.id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const downloadQR = async (link) => {
    try {
      const response = await fetch(
        `${API_BASE}/api/admin/resource-links/${link.id}/qr?size=300&include_logo=true`,
        { headers: getAuthHeaders() }
      );

      if (!response.ok) {
        alert('Failed to generate QR code');
        return;
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `owaza-resource-link-${link.short_code}.svg`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to download QR code:', error);
      alert('Failed to download QR code');
    }
  };

  const previewQR = async (link) => {
    setQrLoading(true);
    setQrPreview({ link, svgUrl: null });

    try {
      const response = await fetch(
        `${API_BASE}/api/admin/resource-links/${link.id}/qr?size=300&include_logo=true`,
        { headers: getAuthHeaders() }
      );

      if (response.ok) {
        const blob = await response.blob();
        const svgUrl = window.URL.createObjectURL(blob);
        setQrPreview({ link, svgUrl });
      }
    } catch (error) {
      console.error('Failed to preview QR code:', error);
    } finally {
      setQrLoading(false);
    }
  };

  const closeQrPreview = () => {
    if (qrPreview?.svgUrl) {
      window.URL.revokeObjectURL(qrPreview.svgUrl);
    }
    setQrPreview(null);
  };

  // Filter links
  const filteredLinks = links.filter((link) => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      link.title?.toLowerCase().includes(term) ||
      link.short_code?.toLowerCase().includes(term) ||
      link.link_type?.toLowerCase().includes(term)
    );
  });

  // Stats
  const activeCount = links.filter((l) => l.is_active).length;
  const totalVisits = links.reduce((sum, l) => sum + (l.visit_count || 0), 0);

  if (loading) {
    return <div className="text-center py-4">Loading resource links...</div>;
  }

  return (
    <div className="space-y-4">
      <PageTitle
        title="Resource Links"
        description="Manage shareable links that deep-link into the member portal"
        icon={<Link2 className="h-6 w-6" />}
      />

      {/* Stats */}
      <div className="grid grid-cols-3 gap-3">
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-gray-800 dark:text-gray-200">{links.length}</p>
            <p className="text-xs text-gray-500 dark:text-gray-400">Total Links</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-green-700 dark:text-green-400">{activeCount}</p>
            <p className="text-xs text-gray-500 dark:text-gray-400">Active</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-blue-700 dark:text-blue-400">{totalVisits}</p>
            <p className="text-xs text-gray-500 dark:text-gray-400">Total Visits</p>
          </CardContent>
        </Card>
      </div>

      {/* Search + Create */}
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search by title, code, or type..."
            className="pl-9"
          />
        </div>
        <Button onClick={() => setShowCreate(true)} className="bg-[#9B1B30] hover:bg-[#7d1526] text-white">
          <Plus className="h-4 w-4 mr-1" /> Create Link
        </Button>
      </div>

      {/* Links List */}
      <div className="space-y-2">
        {filteredLinks.length === 0 ? (
          <div className="text-center py-6 text-gray-500 bg-gray-50 dark:bg-gray-800 rounded">
            {searchTerm ? 'No links match your search.' : 'No resource links created yet.'}
          </div>
        ) : (
          filteredLinks.map((link) => {
            const typeConfig = LINK_TYPE_CONFIG[link.link_type] || LINK_TYPE_CONFIG.resource;
            const shareUrl = `https://members.owazamartialarts.com/r/${link.short_code}`;

            return (
              <div
                key={link.id}
                className={`flex items-center gap-4 p-3 bg-background rounded border hover:shadow-sm transition-shadow ${
                  !link.is_active ? 'opacity-60' : ''
                }`}
              >
                {/* Icon */}
                <div className="p-2 rounded-full bg-indigo-100 dark:bg-indigo-900/50 flex-shrink-0">
                  <Link2 className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-medium truncate">{link.title}</span>
                    <span className={`px-2 py-0.5 text-xs rounded-full ${typeConfig.color}`}>
                      {typeConfig.label}
                    </span>
                    {!link.is_active && (
                      <span className="px-2 py-0.5 bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-400 text-xs rounded">
                        Inactive
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-3 text-sm text-gray-500 dark:text-gray-400 mt-1">
                    <code className="text-xs bg-gray-100 dark:bg-gray-800 px-1.5 py-0.5 rounded">
                      {link.short_code}
                    </code>
                    <span>{link.visit_count || 0} visits</span>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-1 flex-shrink-0">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => copyShareUrl(link)}
                    title="Copy share URL"
                  >
                    {copiedId === link.id ? (
                      <Check className="h-4 w-4 text-green-600" />
                    ) : (
                      <Copy className="h-4 w-4" />
                    )}
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => window.open(shareUrl, '_blank')}
                    title="Open link"
                  >
                    <ExternalLink className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => previewQR(link)}
                    title="Preview QR code"
                  >
                    <QrCode className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => downloadQR(link)}
                    title="Download QR code"
                  >
                    <Download className="h-4 w-4" />
                  </Button>
                  {link.is_active && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDeleteLink(link.id)}
                      title="Deactivate link"
                    >
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Create Link Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="h-5 w-5" />
              Create Resource Link
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <label className="text-sm font-medium mb-1 block">Link Type *</label>
              <Select
                value={newLink.link_type}
                onValueChange={(val) => {
                  setNewLink({ ...newLink, link_type: val, target_id: '' });
                  fetchTargets(val);
                }}
              >
                <SelectTrigger><SelectValue placeholder="Select type..." /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="quiz">Quiz</SelectItem>
                  <SelectItem value="scenario">Scenario</SelectItem>
                  <SelectItem value="vocabulary">Vocabulary</SelectItem>
                  <SelectItem value="resource">Resource (Video/Document)</SelectItem>
                  <SelectItem value="learning_section">Learning Section</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {TARGET_ID_TYPES.includes(newLink.link_type) && targets.length > 0 && (
              <div>
                <label className="text-sm font-medium mb-1 block">Target *</label>
                <Select
                  value={newLink.target_id}
                  onValueChange={(val) => {
                    const target = targets.find(t => t.id === val);
                    setNewLink({
                      ...newLink,
                      target_id: val,
                      title: newLink.title || target?.title || '',
                    });
                  }}
                >
                  <SelectTrigger><SelectValue placeholder="Select content..." /></SelectTrigger>
                  <SelectContent>
                    {targets.map(t => (
                      <SelectItem key={t.id} value={t.id}>{t.title}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div>
              <label className="text-sm font-medium mb-1 block">Title *</label>
              <Input
                value={newLink.title}
                onChange={(e) => setNewLink({ ...newLink, title: e.target.value })}
                placeholder="e.g., White Belt Blocking Techniques"
              />
            </div>

            <div>
              <label className="text-sm font-medium mb-1 block">Description</label>
              <Input
                value={newLink.description}
                onChange={(e) => setNewLink({ ...newLink, description: e.target.value })}
                placeholder="Brief description shown in preview..."
              />
            </div>

            <Button
              onClick={handleCreateLink}
              disabled={creating || !newLink.link_type || !newLink.title}
              className="w-full bg-[#9B1B30] hover:bg-[#7d1526] text-white"
            >
              {creating ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Plus className="h-4 w-4 mr-2" />}
              Create Link
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* QR Preview Dialog */}
      <Dialog open={!!qrPreview} onOpenChange={() => closeQrPreview()}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <QrCode className="h-5 w-5" />
              QR Code: {qrPreview?.link?.title}
            </DialogTitle>
          </DialogHeader>
          <div className="flex flex-col items-center gap-4 py-4">
            {qrLoading ? (
              <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
            ) : qrPreview?.svgUrl ? (
              <img
                src={qrPreview.svgUrl}
                alt={`QR code for ${qrPreview.link?.title}`}
                className="w-64 h-64"
              />
            ) : (
              <p className="text-gray-500">Failed to load QR code</p>
            )}
            <p className="text-sm text-gray-500 dark:text-gray-400 text-center">
              Scan to access:{' '}
              <code className="bg-gray-100 dark:bg-gray-800 px-1.5 py-0.5 rounded text-xs">
                /r/{qrPreview?.link?.short_code}
              </code>
            </p>
            <Button
              onClick={() => qrPreview?.link && downloadQR(qrPreview.link)}
              variant="adminPrimary"
              className="w-full"
            >
              <Download className="h-4 w-4 mr-2" />
              Download SVG
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ResourceLinksManager;
